import { useClerk, useSignIn } from '@clerk/clerk-react';
import { Button, PageHeader, Textfield } from '@flexo/atoms';
import { IconWrapper, REGEX } from '@flexo/general';
import { useFormValidation } from '@flexo/hooks';
import { ALERT } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import * as Sentry from '@sentry/react';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './mobile-log-in.module.scss';

const ENV = (import.meta as unknown as { env: Record<string, string> }).env;

interface LoginInput {
  name: 'email' | 'password';
  label: string;
  placeholder: string;
  description?: string;
}

interface LoginFooter {
  label: string;
  buttonLabel: string;
}

interface LoginContent {
  title: string;
  subtitle?: string;
  inputs: LoginInput[];
  buttonLabel: string;
  footer: LoginFooter;
}

type AuthData = Record<'email' | 'password' | 'createPassword', string>;

export interface MobileLogInProps {
  bgColor?: string;
  containedLabelTextfield?: boolean;
  customTitle?: string;
  showLogo?: boolean;
  textfieldColor?:
    | 'white'
    | 'grey'
    | 'light'
    | 'primary'
    | 'secondary'
    | 'transparent';
  // Add any other props you need
}

export function MobileLogIn({
  bgColor,
  containedLabelTextfield = false,
  customTitle,
  showLogo = true,
  textfieldColor,
}: MobileLogInProps) {
  const {
    LogIn__Inputs,
    LogIn__Footer,
    LogIn__Footer__Register,
    LogIn__Footer__Register__Button,
  } = styles;

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [authData, setAuthData] = useState<AuthData>({
    email: '',
    password: '',
    createPassword: '',
  });
  const [logInClicked, setLogInClicked] = useState(false);
  const [logInError, setLogInError] = useState(false);
  const { t } = useTranslation();

  const [inputType, setInputType] = useState({
    email: 'email',
    password: 'password',
  });

  const [icon, setIcon] = useState('visibility');

  // Email validation function
  const isEmail = (email: string) => {
    return REGEX.email.test(email);
  };

  // Form validation hook - only validate email for login
  const { errors, blur, handleBlur, handleFocus } = useFormValidation(
    {
      fields: ['email'], // Only validate email, not password
      validationRules: {
        email: {
          validate: (value) => isEmail(value),
        },
      },
      hasPasswordFields: false,
    },
    authData as Record<string, string>
  );

  const { title, subtitle, inputs, buttonLabel, footer } = t(
    'pages.login.data',
    { returnObjects: true }
  ) as LoginContent;
  const subtitleText = subtitle ?? '';

  const loader = '/loaderLottie.json';

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setAuthData((prev) => ({ ...prev, [name]: value }));
  };

  const {
    signIn: clerkSignIn,
    isLoaded: isSignInLoaded,
    setActive,
  } = useSignIn();
  const clerk = useClerk();

  const handleSignIn = async () => {
    if (logInClicked || !isSignInLoaded) return;
    setLogInClicked(true);
    setLogInError(false);
    try {
      const result = await clerkSignIn?.create({
        identifier: authData.email,
        password: authData.password,
      });

      if (result?.status === 'complete') {
        await setActive({ session: result.createdSessionId });

        // Set active organization matching this app
        await new Promise((resolve) => setTimeout(resolve, 300));

        const activeUser = clerk.user;
        const orgMemberships = activeUser?.organizationMemberships || [];
        const targetOrgId = ENV.VITE_APP_CLERK_ORG_ID;
        const orgMembership = targetOrgId
          ? orgMemberships.find((m: any) => m.organization.id === targetOrgId) || orgMemberships[0]
          : orgMemberships[0];

        if (orgMembership?.organization) {
          await clerk.setActive({ organization: orgMembership.organization.id });

          // Verify session is no longer pending
          await new Promise((resolve) => setTimeout(resolve, 300));
          const updatedStatus = clerk.session?.status;
          if (updatedStatus === 'pending') {
            try { await setActive({ session: null }); } catch (_) { /* noop */ }
            dispatch(
              ALERT.SetAlert({
                payload: { value: { type: 'BANNER_UNEXPECTED_ERROR' } },
              } as never)
            );
            setLogInClicked(false);
            return;
          }
        }

        dispatch(ALERT.ResetAlert());
        navigate(`/${ENV.VITE_APP_FIRST_PAGE}`);
        return;
      }

      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_UNEXPECTED_ERROR',
            },
          },
        } as never)
      );
    } catch (error) {
      console.error('[MobileLogIn] Clerk sign-in error:', error);
      const errorCode = (error as { errors?: { code?: string }[] })?.errors?.[0]
        ?.code;

      if (
        errorCode === 'form_identifier_not_found' ||
        errorCode === 'form_password_incorrect'
      ) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_WRONG_LOGIN',
              },
            },
          } as never)
        );
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as never)
        );
      }

      setLogInError(true);

      Sentry.captureEvent({
        message: `Clerk sign-in error`,
        release: `${ENV.VITE_APP_VERSION}(${ENV.VITE_APP_BUILD})`,
        level: 'warning',
        extra: {
          error,
        },
      });
      setAuthData({ ...authData, password: '' });
    } finally {
      setLogInClicked(false);
    }
  };

  const togglePasswordVisibility = (name: string) => {
    setInputType((prevState) => ({
      ...prevState,
      [name]: prevState[name] === 'password' ? 'text' : 'password',
    }));
    setIcon(icon === 'visibility' ? 'hide-visibility' : 'visibility');
  };

  const forgotPassword = () => {
    navigate('/forgot-password-mobile');
  };

  const register = () => {
    navigate('/register');
  };

  // Check if user was redirected after password or email update
  useEffect(() => {
    // Check both localStorage and sessionStorage for reliability
    const passwordUpdatedLocal = localStorage.getItem('passwordUpdated');
    const passwordUpdatedSession = sessionStorage.getItem('passwordUpdated');
    const emailUpdatedLocal = localStorage.getItem('emailUpdated');
    const emailUpdatedSession = sessionStorage.getItem('emailUpdated');
    const showInviteAlreadyActivated = sessionStorage.getItem('showInviteAlreadyActivated');
    const showUserAlreadyActive = sessionStorage.getItem('showUserAlreadyActive');

    if (passwordUpdatedLocal === 'true' || passwordUpdatedSession === 'true') {
      // Remove the flags from both storage locations
      localStorage.removeItem('passwordUpdated');
      sessionStorage.removeItem('passwordUpdated');

      // Show password reset success message - ensure we use the correct format
      dispatch(ALERT.ResetAlert());

      // Make sure we're using the correct format for alerts
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_PASSWORD_RESET_SUCCESS',
          },
        } as never)
      );
    } else if (emailUpdatedLocal === 'true' || emailUpdatedSession === 'true') {
      // Remove the flags from both storage locations
      localStorage.removeItem('emailUpdated');
      sessionStorage.removeItem('emailUpdated');

      // Show email update success message
      dispatch(ALERT.ResetAlert());

      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_CONFIRM_CREDENTIALS_CHANGES_SUCCESS',
          },
        } as never)
      );
    } else if (showInviteAlreadyActivated === 'true') {
      // Remove the flag from sessionStorage
      sessionStorage.removeItem('showInviteAlreadyActivated');

      // Show invite already activated error message
      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_INVITE_ALREADY_ACTIVATED',
            },
          },
        } as never)
      );
    } else if (showUserAlreadyActive === 'true') {
      // Remove the flag from sessionStorage
      sessionStorage.removeItem('showUserAlreadyActive');
      const message = sessionStorage.getItem('userAlreadyActiveMessage');
      if (message) {
        sessionStorage.removeItem('userAlreadyActiveMessage');
      }

      // Show user already active message
      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_USER_ALREADY_ACTIVE',
              message: message || 'Questo utente è già attivo. Si prega di eseguire il login.',
            },
          },
        } as never)
      );
    }
  }, [dispatch]); // Run once on mount

  return (
    <div className={styles.LogIn}>
      <PageHeader title={customTitle || title} subtitle={subtitleText} />
      <div
        className={styles.Login__Content}
        style={{ backgroundColor: bgColor ? bgColor : '#F7F7F7' }}
      >
        <div className={LogIn__Inputs}>
          {inputs.map((input) => {
            const isPasswordField = input.name === 'password';

            return (
              <div key={input.name}>
                {isPasswordField ? (
                  <Textfield
                    variant={
                      containedLabelTextfield ? 'containedLabel' : 'default'
                    }
                    name={input.name}
                    label={input.label}
                    type={inputType[input.name]}
                    placeholder={input.placeholder}
                    error={logInError}
                    value={authData[input.name]}
                    onChange={handleInputChange}
                    onPaste={() => false}
                    onCopy={() => false}
                    icon={icon}
                    onIconClick={togglePasswordVisibility}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    autoComplete="current-password"
                    onBlur={undefined}
                    onFocus={() => {
                      if (logInError) setLogInError(false);
                    }}
                    textfieldColor={textfieldColor || 'white'}
                    action={input.description ?? ''}
                    onActionClick={() => forgotPassword()}
                  />
                ) : (
                  <Textfield
                    variant={
                      containedLabelTextfield ? 'containedLabel' : 'default'
                    }
                    name={input.name}
                    label={input.label}
                    type={inputType[input.name]}
                    placeholder={input.placeholder}
                    error={(errors.email && blur.email) || logInError}
                    value={authData[input.name]}
                    onChange={handleInputChange}
                    onPaste={() => false}
                    onCopy={() => false}
                    description={input.description ?? ''}
                    errorDescription={
                      errors.email && blur.email
                        ? 'Inserisci una mail valida'
                        : ''
                    }
                    icon=""
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    autoComplete="email"
                    onBlur={handleBlur}
                    onFocus={(e) => {
                      handleFocus(e);
                      if (logInError) setLogInError(false);
                    }}
                    textfieldColor={textfieldColor || 'white'}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className={LogIn__Footer}>
          <Button
            onClick={handleSignIn}
            label={
              logInClicked && !logInError ? (
                <Player autoplay loop src={loader} style={{ height: 20 }} />
              ) : (
                buttonLabel
              )
            }
            variant="primary"
            color="secondary"
            size="large"
            disabled={
              authData.email === '' ||
              authData.password === '' ||
              (logInClicked && !logInError) ||
              (errors.email && blur.email)
            }
          />
          <span className={`paragraph ${LogIn__Footer__Register}`}>
            {footer.label}
            <button
              className={`paragraph ${LogIn__Footer__Register__Button} `}
              style={{ fontSize: '1em' }}
              onClick={() => register()}
            >
              {footer.buttonLabel}
            </button>
          </span>{' '}
        </div>
        {showLogo && (
          <div className={styles.LogIn__Logo}>
            <IconWrapper iconName="logo" />
          </div>
        )}
      </div>
    </div>
  );
}

export default MobileLogIn;
